<template>
  <div class="pdf-viewer" ref="viewerEl">
    <!-- 工具栏（v-show 避免 DOM 插入/移除导致 canvas 重绘丢失） -->
    <div class="pdf-toolbar" v-show="!loading && !error">
      <button class="pdf-nav-btn" :disabled="currentPage <= 1" @click="prevPage">◀</button>
      <span class="pdf-page-info">{{ currentPage }} / {{ totalPages || '...' }}</span>
      <button class="pdf-nav-btn" :disabled="currentPage >= totalPages" @click="nextPage">▶</button>
    </div>

    <!-- Canvas 渲染区 -->
    <div class="pdf-canvas-wrap" ref="canvasWrap"
      @touchstart="onTouchStart" @touchend="onTouchEnd">
      <canvas ref="canvas" class="pdf-canvas"></canvas>
    </div>

    <!-- 状态 -->
    <div v-show="loading" class="pdf-status">
      <van-loading size="24" color="#fff" />
      <span style="margin-left:8px">PDF 加载中...</span>
    </div>
    <div v-show="error" class="pdf-status pdf-error">{{ error }}</div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist';

// Worker：生产走 /webaikn/，开发走 /
const publicPath = process.env.NODE_ENV === 'production' ? '/webaikn/' : '/';
pdfjsLib.GlobalWorkerOptions.workerSrc = `${publicPath}pdf.worker.min.js`;

export default {
  name: 'PdfViewer',
  props: {
    url: { type: String, required: true },
  },
  data() {
    return {
      pdfDoc: null,
      currentPage: 0,
      totalPages: 0,
      loading: true,
      error: '',
      renderTask: null,
      touchStartX: 0,
      touchStartY: 0,
      isRendering: false,  // 防止并发渲染
      isDestroyed: false,  // 组件已销毁标记
    };
  },
  watch: {
    url(newUrl) {
      if (newUrl && newUrl !== this.lastUrl) {
        this.lastUrl = newUrl;
        this.loadPdf(newUrl);
      }
    },
  },
  mounted() {
    this.lastUrl = this.url;
    if (this.url) this.loadPdf(this.url);
  },
  beforeDestroy() {
    this.isDestroyed = true;
    this.cancelRender();
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
      this.pdfDoc = null;
    }
  },
  methods: {
    // ==== 日志（统一前缀） ====
    dbg(msg) {
      console.log('[PdfViewer]', msg);
    },

    // ==== 加载 PDF ====
    async loadPdf(url) {
      this.dbg('loadPdf: ' + url.substring(0, 80) + '...');
      this.loading = true;
      this.error = '';
      this.currentPage = 0;
      this.totalPages = 0;
      this.isRendering = false;
      this.cancelRender();
      if (this.pdfDoc) {
        this.pdfDoc.destroy();
        this.pdfDoc = null;
      }

      // 清空 canvas
      const canvas = this.$refs.canvas;
      if (canvas) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = 1;
        canvas.height = 1;
        ctx.clearRect(0, 0, 1, 1);
      }

      try {
        const loadingTask = pdfjsLib.getDocument({ url });
        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        this.dbg('加载成功，共 ' + this.totalPages + ' 页');

        // 等待 overlay 动画 + 布局稳定
        await new Promise((r) => setTimeout(r, 300));
        this.dbg('开始渲染第 1 页...');
        await this.renderPage(1);
      } catch (e) {
        this.dbg('加载失败: ' + (e.name || 'Error') + ' - ' + e.message);
        const msg =
          e.name === 'InvalidPDFException'
            ? 'PDF 文件损坏，请尝试下载后查看'
            : e.name === 'MissingPDFException'
              ? 'PDF 文件不存在'
              : 'PDF 加载失败，请尝试下载后查看';
        this.error = msg;
      } finally {
        this.loading = false;
      }
    },

    // ==== 渲染页面（含像素采样诊断） ====
    async renderPage(pageNum) {
      if (!this.pdfDoc || this.isDestroyed) return;

      // 防止并发渲染
      if (this.isRendering) {
        this.dbg('renderPage(' + pageNum + ') 跳过：正在渲染中');
        this.pendingPage = pageNum;
        return;
      }

      this.cancelRender();
      this.isRendering = true;
      this.pendingPage = null;

      const canvas = this.$refs.canvas;
      const container = this.$refs.canvasWrap;
      if (!canvas || !container) {
        this.isRendering = false;
        return;
      }

      // 等待容器尺寸就绪
      for (let retry = 0; retry < 30; retry++) {
        if (container.clientWidth > 0 && container.clientHeight > 0) break;
        await new Promise((r) => setTimeout(r, 30));
      }

      try {
        const containerWidth = container.clientWidth;
        const dpr = window.devicePixelRatio || 1;

        this.dbg('renderPage(' + pageNum + ') container=' + containerWidth + 'x' + container.clientHeight + ' dpr=' + dpr);

        const page = await this.pdfDoc.getPage(pageNum);
        const baseVp = page.getViewport({ scale: 1 });
        this.dbg('renderPage(' + pageNum + ') baseVp=' + baseVp.width.toFixed(0) + 'x' + baseVp.height.toFixed(0));

        const scale = Math.min((containerWidth - 16) / baseVp.width, 2.5);
        const vp = page.getViewport({ scale: Math.max(scale, 0.1) });
        this.dbg('renderPage(' + pageNum + ') scale=' + scale.toFixed(3) + ' vp=' + vp.width.toFixed(0) + 'x' + vp.height.toFixed(0));

        // 设置 canvas 尺寸
        canvas.width = Math.floor(vp.width * dpr);
        canvas.height = Math.floor(vp.height * dpr);
        canvas.style.width = Math.floor(vp.width) + 'px';
        canvas.style.height = Math.floor(vp.height) + 'px';

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 通过 transform 参数处理 DPR
        const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null;

        this.renderTask = page.render({
          canvasContext: ctx,
          viewport: vp,
          transform,
        });
        await this.renderTask.promise;

        // ===== 诊断：getImageData 强制同步 + 多像素采样（与测试页一致） =====
        // 样本1: 左上角(0,0) - 检测 canvas 是否被写过
        const p0 = ctx.getImageData(0, 0, 1, 1).data;
        // 样本2: 中心像素
        const cx = Math.floor(canvas.width / 2);
        const cy = Math.floor(canvas.height / 2);
        const pc = ctx.getImageData(cx, cy, 1, 1).data;
        // 样本3: 距顶部 30px 处（文字通常不会出现在最边缘）
        const pt = ctx.getImageData(cx, 30, 1, 1).data;

        this.dbg('renderPage(' + pageNum + ') 完成, canvas物理=' + canvas.width + 'x' + canvas.height);
        this.dbg('renderPage(' + pageNum + ') ★ 像素(0,0)=rgba(' + p0[0] + ',' + p0[1] + ',' + p0[2] + ',' + p0[3] + ')');
        this.dbg('renderPage(' + pageNum + ') ★ 像素(中心)=rgba(' + pc[0] + ',' + pc[1] + ',' + pc[2] + ',' + pc[3] + ')');
        this.dbg('renderPage(' + pageNum + ') ★ 像素(中上30px)=rgba(' + pt[0] + ',' + pt[1] + ',' + pt[2] + ',' + pt[3] + ')');

        // 判断是否"真正空白"（像素全透明，即没画任何东西）
        const isTrulyBlank = (p) => p[0] === 0 && p[1] === 0 && p[2] === 0 && p[3] === 0;
        const centerBlank = isTrulyBlank(pc);

        if (centerBlank) {
          this.dbg('renderPage(' + pageNum + ') ⚠️ 中心像素空白(全透明)！Canvas 可能未实际绘制');
        } else if (pc[0] === 255 && pc[1] === 255 && pc[2] === 255) {
          this.dbg('renderPage(' + pageNum + ') ℹ️ 中心像素纯白 — 第1页可能本身就是白页/封面');
        } else {
          this.dbg('renderPage(' + pageNum + ') ✅ 中心像素有内容, rgba(' + pc[0] + ',' + pc[1] + ',' + pc[2] + ',' + pc[3] + ')');
        }

        this.currentPage = pageNum;
      } catch (e) {
        if (e.name !== 'RenderingCancelledException') {
          this.dbg('渲染异常: ' + e.message);
        }
      } finally {
        this.isRendering = false;
        // 如果有被跳过的页码请求，渲染它
        if (this.pendingPage && !this.isDestroyed) {
          const p = this.pendingPage;
          this.pendingPage = null;
          this.dbg('补渲被跳过的页码: ' + p);
          this.renderPage(p);
        }
      }
    },

    cancelRender() {
      if (this.renderTask) {
        try { this.renderTask.cancel(); } catch (e) { /* ignore */ }
        this.renderTask = null;
      }
    },

    prevPage() {
      if (this.currentPage > 1) this.renderPage(this.currentPage - 1);
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.renderPage(this.currentPage + 1);
    },

    /* 手势滑动 */
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      const dy = e.changedTouches[0].clientY - this.touchStartY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        dx < 0 ? this.nextPage() : this.prevPage();
      }
    },
  },
};
</script>

<style scoped>
.pdf-viewer {
  width: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  background: #1a1a2e;
  overflow: hidden;
}
.pdf-toolbar {
  display: flex; align-items: center; justify-content: center; gap: 16px;
  padding: 8px 12px; flex-shrink: 0; background: rgba(0,0,0,0.3);
}
.pdf-nav-btn {
  width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(255,255,255,0.2); border-radius: 50%;
  background: transparent; color: #fff; cursor: pointer; font-size: 14px;
}
.pdf-nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.pdf-page-info {
  font-size: 13px; color: rgba(255,255,255,0.8); min-width: 70px; text-align: center;
}
.pdf-canvas-wrap {
  flex: 1; overflow: auto; display: flex; justify-content: center; align-items: flex-start;
  padding: 8px;
  -webkit-overflow-scrolling: touch; touch-action: pan-y;
}
.pdf-canvas {
  display: block; max-width: 100%;
  box-shadow: 0 2px 16px rgba(0,0,0,0.4);
  /* 不再设置 background: #fff，避免掩盖渲染结果 */
}
.pdf-status {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  display: flex; align-items: center; color: rgba(255,255,255,0.8); font-size: 14px;
}
.pdf-error { color: #ff6b6b; }
</style>
